// Press brand assets — typed RPC surface.
//
// Public read for the EPK; admin-gated writes from /admin/press-room.
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { assertAdmin } from "@/lib/shop.server";
import { PRESS_SLOT_IDS, type PressBrandAssetMap } from "@/lib/press-brand-assets";
import { readPressBrandAssets } from "@/lib/press-brand-assets.server";

export const getPressBrandAssets = createServerFn({ method: "GET" }).handler(
  async (): Promise<PressBrandAssetMap> => readPressBrandAssets(),
);

const saveSchema = z.object({
  slot: z.string().refine((s) => PRESS_SLOT_IDS.includes(s), "Unknown press asset slot"),
  url: z.string().url().max(2000),
  alt: z.string().max(300).optional(),
  credit: z.string().max(200).optional(),
});

export const savePressBrandAsset = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) => saveSchema.parse(data))
  .handler(async ({ data, context }): Promise<PressBrandAssetMap> => {
    await assertAdmin(context as never);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { error } = await (supabaseAdmin.from("press_brand_assets") as any).upsert(
      {
        slot: data.slot,
        url: data.url,
        alt: data.alt ?? "",
        credit: data.credit?.trim() || "Courtesy of Buddha KA / KAfrequency",
        updated_at: new Date().toISOString(),
        updated_by: (context as { userId: string }).userId,
      },
      { onConflict: "slot" },
    );
    if (error) throw new Error(error.message);
    return readPressBrandAssets();
  });

export const clearPressBrandAsset = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) =>
    z.object({ slot: z.string().refine((s) => PRESS_SLOT_IDS.includes(s)) }).parse(data),
  )
  .handler(async ({ data, context }): Promise<PressBrandAssetMap> => {
    await assertAdmin(context as never);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { error } = await (supabaseAdmin.from("press_brand_assets") as any)
      .delete()
      .eq("slot", data.slot);
    if (error) throw new Error(error.message);
    return readPressBrandAssets();
  });
